import React, { createContext, useContext, useState, useEffect } from 'react';
import { getStoredApiKey, storeApiKey, removeStoredApiKey } from '../utils/apiKeyUtils';
import { validateApiKeyFormat, testApiKey } from '../services/openaiService';

// Create the context
const ApiKeyContext = createContext();

// Custom hook to use the API key context
export const useApiKey = () => {
  const context = useContext(ApiKeyContext);
  if (!context) {
    throw new Error('useApiKey must be used within an ApiKeyProvider');
  }
  return context;
};

// API Key Provider component
export const ApiKeyProvider = ({ children }) => {
  const [apiKey, setApiKey] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('');
  const [isValidating, setIsValidating] = useState(false);

  // Load API key from cookies on mount
  useEffect(() => {
    const savedApiKey = getStoredApiKey();
    if (savedApiKey) {
      setApiKey(savedApiKey);
      if (validateApiKeyFormat(savedApiKey)) {
        setIsConnected(true);
        setConnectionStatus('✅ Connected (loaded from saved session)');
      }
    }
  }, []);

  // Connect function with real API validation
  const connect = async (keyToTest = apiKey) => {
    if (!keyToTest.trim()) {
      setConnectionStatus('❌ Please enter an API key');
      return false;
    }

    setIsValidating(true);
    setConnectionStatus('🔄 Validating API key...');

    try {
      // First check format
      if (!validateApiKeyFormat(keyToTest)) {
        setConnectionStatus('❌ Invalid API key format. Please check that it starts with "sk-" and is complete.');
        setIsConnected(false);
        return false;
      }

      // Then test with actual API call
      await testApiKey(keyToTest);
      
      // If we get here, the API key is valid
      setApiKey(keyToTest);
      setIsConnected(true);
      setConnectionStatus('✅ Connected successfully!');
      storeApiKey(keyToTest);
      return true;

    } catch (error) {
      setIsConnected(false);
      setConnectionStatus(`❌ ${error.message}`);
      return false;
    } finally {
      setIsValidating(false);
    }
  };

  // Disconnect function
  const disconnect = () => {
    setApiKey('');
    setIsConnected(false);
    setConnectionStatus('');
    removeStoredApiKey();
  };

  // Update API key (for input field)
  const updateApiKey = (newKey) => {
    setApiKey(newKey);
    // Clear status when user starts typing
    if (connectionStatus && !isValidating) {
      setConnectionStatus('');
    }
  };

  const value = {
    apiKey,
    isConnected,
    connectionStatus,
    isValidating,
    connect,
    disconnect,
    updateApiKey
  };

  return (
    <ApiKeyContext.Provider value={value}>
      {children}
    </ApiKeyContext.Provider>
  );
}; 